/**
 * Shared types between server and frontend.
 * Keep this file free of Bun/Node APIs — it is bundled into the browser.
 */

/** Allowed values for a tool's release status badge. */
export const TOOL_STATUSES = ["released", "beta", "early-access"] as const;
export type ToolStatus = (typeof TOOL_STATUSES)[number];

export const TOOL_STATUS_LABELS: Record<ToolStatus, string> = {
  released: "Released",
  beta: "Beta",
  "early-access": "Early Access",
};

export interface ChangelogEntry {
  /** Version label, e.g. "1.2.0" or "2026-07". */
  version: string;
  /** Optional release date (ISO string recommended). */
  date?: string;
  /** Optional update notes for this version. */
  notes?: string;
}

export interface Tool {
  /** Stable identifier, derived from the filename (e.g. "json-formatter"). */
  id: string;
  /** Human-readable display name. */
  name: string;
  /** One-sentence summary shown on the card. */
  description: string;
  /** External URL the card links to. */
  url: string;
  /** One or more categories the tool is browsable under. */
  categories: string[];
  /** Optional extra search keywords that are not shown in the UI. */
  keywords?: string[];
  /** Featured tools sort before the rest within equal search scores. */
  featured?: boolean;
  /** Release status shown as a badge pill on the card. */
  status?: ToolStatus;
  /** Optional longer Markdown description (the body of the tool file). */
  details?: string;
  /** Optional version history, newest entry first. */
  changelog?: ChangelogEntry[];
}

export interface ToolsResponse {
  tools: Tool[];
  /** Sorted, de-duplicated list of every category in use. */
  categories: string[];
}
